import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import { SchedulablePresenter } from 'shared/presenters/SchedulablePresenter'
import {
  LineItemInterface,
  StyledLineItem,
  StyledFlex,
  StyledDetailsFlex,
  StyledInfoFlex
} from './LineItem'
import Button from 'library/atoms/Button'
import { defined, definedNotNull } from 'shared/presenters/presenter'
import { Text } from 'library/atoms/Typography'
import Flex from 'shared/library/atoms/Flex'
import Thumbnail from 'shared/library/atoms/Thumbnail'
import Price from 'shared/library/atoms/Price'
import { determineChargeableReviewDataForSchedulables } from 'helpers/determineChargeableReviewData'
import { OptionalExtraPresenter } from 'shared/presenters'
import formatOptionalExtras from 'shared/helpers/formatOptionalExtraData/formatOptionalExtras'
import Icon from 'shared/library/atoms/Icon'
import { BookingPresenter } from 'shared/presenters/BookingPresenter'
import { StyledExpandIcon } from 'library/molecules/DetailsList'

const LineItemActivityVoucher = ({
  outlet,
  item,
  removeItemOnClick,
  removeItemLoading,
  setExpanded,
  expanded
}: LineItemInterface) => {
  const { t } = useTranslation('common')

  const booking =
    item?.activity_voucher?.booking &&
    new BookingPresenter(item?.activity_voucher?.booking)

  const schedulable = new SchedulablePresenter(booking?.schedulable || {})

  const generateDataForChargeableReview = () => {
    // OptionalExtra
    const chosenOptionalExtras = defined(booking?.chosen_optional_extras)
    const optionalExtrasQuantities = booking?.getOptionalExtrasQuantities()

    const optionalExtraPresenter = chosenOptionalExtras?.map(
      item => new OptionalExtraPresenter(definedNotNull(item.optional_extra))
    )

    const optionalExtras = formatOptionalExtras(
      optionalExtraPresenter,
      optionalExtrasQuantities
    )

    return determineChargeableReviewDataForSchedulables({
      chargeable: schedulable,
      adults: defined(booking?.adultsGoing()),
      children: defined(booking?.childrenGoing()),
      isGift: true,
      optionalExtras,
      exclusive: defined(booking?.exclusive),
      total: defined(item.price_paid),
      t
    })
  }

  const chargeableReviewData = generateDataForChargeableReview()

  return (
    <StyledLineItem key={schedulable.id}>
      <Flex
        alignItems="center"
        onClick={() => {
          if (setExpanded) setExpanded(!expanded)
        }}
      >
        <Thumbnail src={schedulable.imageURL()} thumbnailSize={40} />
        <StyledFlex>
          <Text>{schedulable.name()}</Text>
          <Flex alignItems="center" gap={3}>
            <Icon name="gift" color="primary" size={20} />
            <Price
              pence={defined(item.price_paid)}
              currency={outlet.currency()}
            />
            <StyledExpandIcon expanded={!!expanded} />
          </Flex>
        </StyledFlex>
      </Flex>
      {expanded && (
        <>
          <StyledDetailsFlex>
            {chargeableReviewData?.lineItemSummary.participants && (
              <StyledInfoFlex>
                <Icon name="profile" size={20} mr={3} />
                <Text color="secondary">
                  {chargeableReviewData.lineItemSummary.participants}
                </Text>
              </StyledInfoFlex>
            )}
            {chargeableReviewData?.lineItemSummary.addons && (
              <StyledInfoFlex>
                <Icon name="add" size={20} mr={3} />
                <Text color="secondary">
                  {chargeableReviewData.lineItemSummary.addons}
                </Text>
              </StyledInfoFlex>
            )}
            {chargeableReviewData?.lineItemSummary.exclusive && (
              <StyledInfoFlex>
                <Icon name="star-outline" size={20} mr={3} />
                <Text color="secondary">{t('exclusive')}</Text>
              </StyledInfoFlex>
            )}
          </StyledDetailsFlex>
          {(removeItemOnClick || chargeableReviewData) && (
            <>
              <Flex mt={4} justifyContent="end">
                {removeItemOnClick && (
                  <Button
                    variant="tertiary"
                    loading={removeItemLoading}
                    inactive={removeItemLoading}
                    onClick={() => {
                      removeItemOnClick(definedNotNull(item.id))
                    }}
                    aria-label={t('remove-booking')}
                  >
                    {t('delete')}
                  </Button>
                )}
              </Flex>
            </>
          )}
        </>
      )}
    </StyledLineItem>
  )
}

export default LineItemActivityVoucher
